/**
 * Renders the Bifrost Detail Component.
 *
 * @param {Object} chosenOrg - The chosen organization object.
 * @returns {JSX.Element} The rendered Bifrost Detail Component.
 */
import { useEntriesV2 } from "@/Hooks"
import { createContext, useContext, useEffect, useMemo, useRef } from "react"

import Loader from "../../Loader/Loader"
import "./DeviceDetailsComponent.scss"

import { useAuth0 } from "@auth0/auth0-react"
import { useParams } from "react-router-dom"
import { useGetDeviceConfigById } from "../../../../Hooks/FetchHooks/GetDataService/useGetDataById/useGetDeviceConfigById"
import { RebootDeviceModal } from "../../../../Pages/MainPanel/MainPanelBody/DevicesPage/DeviceDetailsPage/RebootDeviceModal/RebootDeviceModal"
import { hasFeatureInPackage, isSuperAdminOrResellerAdminOrAdmin, isVersionHigherOrEqual } from "../../../../Utils/util"
import userInfoContext from "../../../../store/userInfoContext"
import EndOngoingSessionModal from "../../../BifrostDeviceDetailsComponents/DeviceOpration/EndOngoingSessionModal/template/EndOngoingSessionModal"
import DirectTunnelSettings from "../../../BifrostDeviceDetailsComponents/DirectTunnelSettings/template/DirectTunnelSettings"
import BifrostTab from "../../../BifrostElementComponent/BifrostTab/BifrostTab"
import DeviceDetailsSegment from "./DeviceDetailsSegment"
import DeviceSettingsSegment from "./DeviceSettingsSegment"

export const DeviceDetailsContext = createContext({
	unitConfig: {
		unitConfigData: {},
		getUnitConfigure: () => {}
	}
})

const DeviceDetailsComponent = ({ data, deviceIsLoading, getDeviceById, latestFirmwareVersion, firmwareUpToDate }) => {
	const { id } = useParams()
	const { user } = useAuth0()
	const userInfo = useContext(userInfoContext)
	const ref = useRef()

	const { isLoading: isLoadingGetDeviceConfigById, data: deviceConfigData, isError: isErrorGetDeviceConfigById, getDeviceConfigById } = useGetDeviceConfigById(id)
	const [unitConfigureData, { getData: getUnitConfigure }] = useEntriesV2(`/api/v2/device/configure/${id}`)

	const detailData = useMemo(() => {
		return [
			{
				tabName: "Details",
				feature: true,
				featureName: null,
				featureIcon: "sliders",
				renderCondition: false,
				disabled: false,
				isLoading: false,
				tabContent: <DeviceDetailsSegment data={data} getDeviceById={getDeviceById} latestFirmwareVersion={latestFirmwareVersion} firmwareUpToDate={firmwareUpToDate} />
			},
			{
				tabName: "Settings",
				feature: hasFeatureInPackage(userInfo, "device_config"),
				featureName: "device_config",
				featureIcon: "setting",
				fetchData: getDeviceConfigById,
				disabled: false,
				isLoading: isLoadingGetDeviceConfigById,
				isError: isErrorGetDeviceConfigById,
				tabContent: (
					<DeviceSettingsSegment
						data={data}
						deviceConfigData={deviceConfigData}
						latestFirmwareVersion={latestFirmwareVersion}
						firmwareUpToDate={firmwareUpToDate}
						getDeviceById={getDeviceById}
					/>
				)
			},
			{
				tabName: "Direct Tunnel Settings",
				feature: true,
				// feature: hasFeatureInPackage(userInfo, "direct_tunnel"),
				featureName: null,
				// featureName: 'direct_tunnel',
				featureIcon: "exchange",
				isBetaFeature: false,
				renderCondition: !(isSuperAdminOrResellerAdminOrAdmin(user) && (process.env.REACT_APP_THEME === "bifrost" || process.env.REACT_APP_THEME === "advenica")), // If the condition is false or null then the tab will be rendered
				disabled: false,
				isLoading: false,
				/** ref is used for refresh button, data is the device data from getDiviceById */
				tabContent: <DirectTunnelSettings ref={ref} data={data} getDeviceById={getDeviceById} />
			}
		]
	}, [data, getDeviceById, latestFirmwareVersion, firmwareUpToDate, userInfo, isLoadingGetDeviceConfigById, isErrorGetDeviceConfigById])

	const settings = useMemo(() => {
		return {
			refreshs: [
				{
					tab: "Details",
					refresh: getDeviceById
				},
				{
					tab: "Settings",
					refresh: () => {
						getDeviceConfigById()
					}
				},
				{
					tab: "Direct Tunnel Settings",
					refresh: () => {
						ref.current.refreshServiceStatus(true)
						getUnitConfigure()
					}
				}
			],
			options: [
				<EndOngoingSessionModal key="endSession" chosenItem={data} refresh={getDeviceById} />,
				<RebootDeviceModal key="reboot" chosenItem={data} disabled={data.state !== "idle" || !isVersionHigherOrEqual(data.firmware, "v4.9.0")} />
			]
		}
	}, [data])

	const style = useMemo(() => {
		return {
			tabular: false,
			tabStyle: { marginBottom: "0px" },
			contentStyle: { padding: "0px" }
		}
	}, [])

	useEffect(() => {
		getUnitConfigure()
	}, [])

	return (
		<DeviceDetailsContext.Provider
			value={{
				unitConfig: {
					unitConfigData: unitConfigureData,
					getUnitConfigure: getUnitConfigure
				}
			}}
		>
			<div className={"device-detail-container"}>
				{deviceIsLoading ? (
					<Loader backgroundColor="white" inverted height="1px" minHeight="200px" />
				) : (
					<div className={"device-detail-segment"}>
						<BifrostTab detailData={detailData} settings={settings} style={style} />
					</div>
				)}
			</div>
		</DeviceDetailsContext.Provider>
	)
}

export default DeviceDetailsComponent
